/**
 * Peeqd Tools Registry - Instagram tracking tools
 */

export type Platform = "instagram" | "tiktok";

export type ReviewVariant =
  | "markdown"
  | "structured"
  | "table"
  | "translation";

export type OriginalViewerMode = "image" | "pdf" | "doc-icon" | "auto";

export interface ToolReconciliationConfig {
  enabled: true;
  minDocs: number;
  buttonLabel?: string;
  variant?: "invoice" | "receipt" | "bankStatement" | "resume" | "contract";
}

export interface ToolUIConfig {
  reviewVariant: ReviewVariant;
  originalViewer: OriginalViewerMode;
  exportFormats?: Array<"txt" | "word" | "excel" | "csv" | "json">;
  reconciliation?: ToolReconciliationConfig | null;
  hasOptionsSlot?: boolean;
  reviewNote?: string;
}

export interface FreeTool {
  id: string;
  slug: string;
  title: string;
  shortTitle: string;
  description: string;
  metaTitle?: string;
  metaDescription: string;
  keywords: string[];
  icon: string;
  platform: Platform;
  estimatedTime: string;
  dailyFreeLimit: number;
  /** Legacy document-tool fields - optional, kept for editor/processing infra */
  category?: "extraction" | "conversion" | "analysis";
  acceptedFiles?: string;
  outputFormat?: string;
  uiConfig?: ToolUIConfig;
}

export const FREE_TOOLS: FreeTool[] = [
  {
    id: "followers-following",
    slug: "followers-following",
    title: "Followers & Following Tracker",
    shortTitle: "Followers & Following",
    metaTitle: "Instagram Followers & Following Tracker - Real Timestamps",
    description:
      "Track who followed, unfollowed, and who they started following - every change tracked from the moment you start.",
    metaDescription:
      "Track Instagram followers and following changes with real timestamps. See who someone recently followed or unfollowed. Free to try, no signup.",
    keywords: [
      "instagram followers tracker",
      "recent followers",
      "recent following",
      "who did they follow",
      "instagram unfollow tracker",
    ],
    icon: "tracker",
    platform: "instagram",
    estimatedTime: "Instant",
    dailyFreeLimit: 5,
  },
  {
    id: "story-alerts",
    slug: "story-alerts",
    title: "Instagram Story Alerts",
    shortTitle: "Story Alerts",
    metaTitle: "Instagram Story Alerts - Anonymous Story Notifications",
    description:
      "Get pinged the second any public account posts a story - view anonymously before it expires.",
    metaDescription:
      "Instagram story alerts for any public account. Get notified when someone posts a story, even if you don't follow them. Anonymous viewing, no media stored.",
    keywords: [
      "instagram story alerts",
      "story notification",
      "anonymous story viewer",
      "story tracker",
    ],
    icon: "stories",
    platform: "instagram",
    estimatedTime: "Instant",
    dailyFreeLimit: 5,
  },
  {
    id: "competitor-watch",
    slug: "competitor-watch",
    title: "Competitor Watch - Instagram Digest",
    shortTitle: "Competitor Watch",
    metaTitle: "Instagram Competitor Watch - Daily Follow Digest",
    description:
      "Track up to 40 competitors and get a daily digest of follows, unfollows, and story cadence.",
    metaDescription:
      "Watch Instagram competitors with daily digests. Track follows, unfollows, follower swings and story activity across up to 40 accounts.",
    keywords: [
      "instagram competitor tracker",
      "competitor watch",
      "instagram digest",
      "follow tracker",
    ],
    icon: "competitors",
    platform: "instagram",
    estimatedTime: "Daily digest",
    dailyFreeLimit: 3,
  },
  {
    id: "audience-audit",
    slug: "audience-audit",
    title: "Instagram Audience Audit",
    shortTitle: "Audience Audit",
    metaTitle: "Instagram Audience Audit - Real vs Bot Followers",
    description:
      "Break down any public account by real vs suspicious followers, engagement rate and growth trend.",
    metaDescription:
      "Audit any Instagram account's audience. Real vs bot followers, engagement rate, and growth trends before you spend on a partnership.",
    keywords: [
      "instagram audience audit",
      "fake followers check",
      "engagement rate",
      "follower quality",
    ],
    icon: "audit",
    platform: "instagram",
    estimatedTime: "~30 seconds",
    dailyFreeLimit: 3,
  },
];

export const PLATFORM_CATEGORIES = [
  {
    id: "instagram" as const,
    name: "Instagram",
    tools: FREE_TOOLS.filter((t) => t.platform === "instagram"),
    comingSoon: false,
  },
  {
    id: "tiktok" as const,
    name: "TikTok",
    tools: [] as FreeTool[],
    comingSoon: true,
  },
];

export const getToolBySlug = (slug: string): FreeTool | undefined =>
  FREE_TOOLS.find((tool) => tool.slug === slug);

export const getToolById = (id: string): FreeTool | undefined =>
  FREE_TOOLS.find((tool) => tool.id === id);

export const getInstagramTools = () =>
  FREE_TOOLS.filter((t) => t.platform === "instagram");

export const DEFAULT_TOOL_SLUG = "followers-following";

export const TOOLS_MENU = {
  categories: PLATFORM_CATEGORIES.filter((c) => !c.comingSoon).map((c) => ({
    name: c.name,
    icon: c.id === "instagram" ? "📸" : "🎵",
    tools: c.tools,
  })),
  featured: ["followers-following", "story-alerts", "competitor-watch"],
};

export const RATE_LIMIT_CONFIG = {
  storageKeyPrefix: "peeqd_tool_",
  emailUnlockBonus: 10,
  getStorageKey: (toolId: string) => `peeqd_tool_${toolId}`,
};
